const isEmpty = (value) => {
  if (value === '' || value === undefined || value === null) return true
  if (Array.isArray(value) && value.length === 0) return true
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).length === 0
  ) {
    return true
  }
  return false
}

const isNumber = (value) => {
  if (value === '' || value === null || value === undefined) return false
  return !Number.isNaN(Number(value))
}

const normalizeValue = (value) => {
  if (value === 'true') return true
  if (value === 'false') return false
  if (isNumber(value)) return Number(value)
  return value
}

const toValueList = (value) => {
  if (Array.isArray(value)) return value
  if (typeof value === 'string')
    return value.split(',').map((item) => item.trim())
  return [value]
}

const createRuleDateValue = (year, month, day) => {
  year = Number(year)
  month = Number(month)
  day = Number(day)

  if (year > 2400) year = year - 543

  let dateValue = Date.UTC(year, month - 1, day)
  let date = new Date(dateValue)

  if (
    Number.isNaN(dateValue) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return NaN
  }

  return dateValue
}

const getRuleDateValue = (value) => {
  if (value === '' || value === undefined || value === null) return NaN

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return NaN

    return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())
  }

  let text = String(value || '').trim()
  let yearMonthDay = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)

  if (yearMonthDay) {
    return createRuleDateValue(
      yearMonthDay[1],
      yearMonthDay[2],
      yearMonthDay[3],
    )
  }

  let dayMonthYear = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)

  if (dayMonthYear) {
    return createRuleDateValue(
      dayMonthYear[3],
      dayMonthYear[2],
      dayMonthYear[1],
    )
  }

  let date = new Date(value)

  if (Number.isNaN(date.getTime())) return NaN

  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
}

const compareRuleDateValue = (value, conditionValue, compareDate) => {
  let checkDate = getRuleDateValue(value)
  let compareDateValue = getRuleDateValue(conditionValue)

  if (Number.isNaN(checkDate) || Number.isNaN(compareDateValue)) return false

  return compareDate(checkDate, compareDateValue)
}

const isRuleDateCompare = (condition = {}) => {
  let tag = condition.compareTo?.tag || condition.tag || condition.dataType || ''

  return ['date', 'dateTime'].includes(tag)
}

export const isValidThaiCitizenId = (value) => {
  let citizenId = String(value || '').trim()

  if (!/^\d{13}$/.test(citizenId)) return false

  let sum = citizenId
    .slice(0, 12)
    .split('')
    .reduce((total, digit, index) => {
      return total + Number(digit) * (13 - index)
    }, 0)
  let checkDigit = (11 - (sum % 11)) % 10

  return checkDigit === Number(citizenId[12])
}

const isInvalidThaiCitizenId = (value) => {
  if (isEmpty(value)) return false

  return !isValidThaiCitizenId(value)
}

const compareRuleLengthValue = (value, condition = {}) => {
  let length = String(value || '').length
  let compareLength = Number(condition.value || 0)

  switch (condition.lengthCompare || 'isEqual') {
    case 'isNotEqual':
      return length !== compareLength
    case 'isGreater':
      return length > compareLength
    case 'isLess':
      return length < compareLength
    case 'isGreaterOrEqual':
      return length >= compareLength
    case 'isLessOrEqual':
      return length <= compareLength
    case 'isEqual':
    default:
      return length === compareLength
  }
}

export const compareRuleValue = (
  compare,
  value,
  conditionValue,
  condition = {},
) => {
  let checkValue = normalizeValue(value)
  let compareValue = normalizeValue(conditionValue)

  switch (compare) {
    case 'isEmpty':
      return isEmpty(value)
    case 'isFilled':
      return !isEmpty(value)
    case 'isEqual':
      return checkValue === compareValue
    case 'isNotEqual':
      return checkValue !== compareValue
    case 'isContained':
      return toValueList(value).map(normalizeValue).includes(compareValue)
    case 'isNotContained':
      return !toValueList(value).map(normalizeValue).includes(compareValue)
    case 'isAllOf':
      return toValueList(compareValue).every((item) =>
        toValueList(checkValue)
          .map(normalizeValue)
          .includes(normalizeValue(item)),
      )
    case 'isAnyOf':
      return toValueList(compareValue).some((item) =>
        toValueList(checkValue)
          .map(normalizeValue)
          .includes(normalizeValue(item)),
      )
    case 'isGreater':
      if (isRuleDateCompare(condition)) {
        return compareRuleDateValue(value, conditionValue, (a, b) => a > b)
      }
      return Number(checkValue) > Number(compareValue)
    case 'isLess':
      if (isRuleDateCompare(condition)) {
        return compareRuleDateValue(value, conditionValue, (a, b) => a < b)
      }
      return Number(checkValue) < Number(compareValue)
    case 'isGreaterOrEqual':
      if (isRuleDateCompare(condition)) {
        return compareRuleDateValue(value, conditionValue, (a, b) => a >= b)
      }
      return Number(checkValue) >= Number(compareValue)
    case 'isLessOrEqual':
      if (isRuleDateCompare(condition)) {
        return compareRuleDateValue(value, conditionValue, (a, b) => a <= b)
      }
      return Number(checkValue) <= Number(compareValue)
    case 'isDateEqual':
      return compareRuleDateValue(value, conditionValue, (a, b) => a === b)
    case 'isDateNotEqual':
      return compareRuleDateValue(value, conditionValue, (a, b) => a !== b)
    case 'isDateAfter':
      return compareRuleDateValue(value, conditionValue, (a, b) => a > b)
    case 'isDateBefore':
      return compareRuleDateValue(value, conditionValue, (a, b) => a < b)
    case 'isDateAfterOrEqual':
      return compareRuleDateValue(value, conditionValue, (a, b) => a >= b)
    case 'isDateBeforeOrEqual':
      return compareRuleDateValue(value, conditionValue, (a, b) => a <= b)
    case 'isCheckLength':
      return compareRuleLengthValue(value, condition)
    case 'isValidThaiCitizenId':
      return isValidThaiCitizenId(value)
    case 'isInvalidThaiCitizenId':
      return isInvalidThaiCitizenId(value)
    case 'isShow':
    case 'isOnClick':
    case 'isAfterSuccess':
    case 'isHide':
    case 'isEnable':
    case 'isDisable':
    case 'isCondition':
    case 'isLocked':
    case 'isCompareOption':
      return Boolean(value)
    default:
      return false
  }
}
